// Web → Mapa webu (owner only).
// Editovatelný přehled všech stránek fitway40.cz: URL, účel, indexace, SEO, marketingové kódy.
// Data z /api/web-mapa (Netlify Blobs, izolované úložiště – nesahá na nic z provozu).
//
// Zobrazení (25.8.2026): stejný formát jako Log změn webu. Přehledné řádky
// jako v Klientech, klik rozbalí detail jako v Hlášení, editace vždy jen
// u jedné stránky. Dřív to byl seznam rozbalených karet, kde se v pětadvaceti
// stránkách nedalo nic najít.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Globe, Loader2, ExternalLink, Plus, Trash2, Save, Pencil, X, DownloadCloud,
  Search, ChevronDown, ChevronUp,
} from "lucide-react";
import { apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote } from "../../components/ui.jsx";
import { toast } from "../../components/Toast.jsx";

const POLE = [
  { key: "nazev", label: "Název", type: "text" },
  { key: "url", label: "URL", type: "text" },
  { key: "ucel", label: "Účel / funnel", type: "textarea" },
  { key: "indexace", label: "Indexace", type: "select", options: ["index", "noindex", "n/a"] },
  { key: "seoTitle", label: "SEO titulek", type: "text" },
  { key: "seoDesc", label: "SEO popis", type: "textarea" },
  { key: "kody", label: "Marketingové kódy", type: "textarea" },
  { key: "poznamka", label: "Poznámka", type: "textarea" },
];

const INDEX_BADGE = {
  index: "bg-emerald-100 text-emerald-700 border-emerald-200",
  noindex: "bg-amber-100 text-amber-700 border-amber-200",
  "n/a": "bg-slate-100 text-slate-500 border-slate-200",
};

const INDEX_PRUH = {
  index: "bg-emerald-500",
  noindex: "bg-amber-500",
  "n/a": "bg-slate-300",
};


/** Adresa bez protokolu a domény, ať je v řádku vidět to podstatné. */
function kratkaUrl(url) {
  return String(url || "").replace(/^https?:\/\//, "").replace(/^fitway40\.cz/, "") || "/";
}

function prazdnaStranka() {
  return {
    id: `p_${Date.now()}`,
    nazev: "",
    url: "https://fitway40.cz/",
    ucel: "",
    indexace: "index",
    seoTitle: "",
    seoDesc: "",
    kody: "",
    poznamka: "",
  };
}

export default function WebMapa() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [chybejici, setChybejici] = useState([]);

  const [otevrene, setOtevrene] = useState(null);
  const [upravovane, setUpravovane] = useState(null);
  const [koncept, setKoncept] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const [hledej, setHledej] = useState("");
  const [filtrIndex, setFiltrIndex] = useState("vse");

  const detailRefs = useRef({});

  const load = () => {
    setLoading(true);
    setErr(null);
    apiFetch("/api/web-mapa")
      .then((r) => r.json())
      .then((j) => {
        if (j.ok) {
          setPages(j.pages || []);
          setChybejici(j.chybejici || []);
          setUpdatedAt(j.updatedAt || null);
        } else {
          setErr(j.error || "Chyba načítání");
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Po rozbalení plynule doskrolovat k obsahu, ať je vidět, co se otevřelo.
  useEffect(() => {
    if (!otevrene) return;
    const el = detailRefs.current[otevrene];
    if (el && el.scrollIntoView) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
      return () => clearTimeout(t);
    }
  }, [otevrene, upravovane]);

  const prepnout = (id) => {
    if (upravovane) return;
    setOtevrene((p) => (p === id ? null : id));
  };

  const zacitUpravu = (p) => {
    setKoncept({ ...p });
    setUpravovane(p.id);
    setOtevrene(p.id);
  };

  const zrusitUpravu = () => {
    setUpravovane(null);
    setKoncept(null);
    setConfirmDel(null);
    setErr(null);
  };

  const uloz = (novaData, poUlozeni) => {
    setSaving(true);
    setErr(null);
    apiFetch("/api/web-mapa", { method: "POST", body: JSON.stringify({ pages: novaData }) })
      .then((r) => r.json())
      .then((j) => {
        if (j.ok) {
          setPages(j.pages || novaData);
          setUpdatedAt(j.updatedAt || null);
          if (poUlozeni) poUlozeni();
          load(); // znovu spočítat, co ještě chybí
        } else {
          setErr(j.error || "Uložení selhalo");
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setSaving(false));
  };

  const ulozKoncept = () => {
    if (!koncept) return;
    const existuje = pages.some((p) => p.id === koncept.id);
    const nova = existuje
      ? pages.map((p) => (p.id === koncept.id ? koncept : p))
      : [...pages, koncept];
    uloz(nova, () => {
      setUpravovane(null);
      setKoncept(null);
    });
  };

  const smaz = (id) => {
    uloz(pages.filter((p) => p.id !== id), () => {
      setConfirmDel(null);
      setUpravovane(null);
      setKoncept(null);
      setOtevrene(null);
    });
  };

  const nova = () => {
    const p = prazdnaStranka();
    setKoncept(p);
    setUpravovane(p.id);
    setOtevrene(p.id);
    setPages((prev) => [...prev, p]); // jen v UI, uloží se až tlačítkem Uložit
  };

  // Doplní stránky, které na webu existují, ale v uložené mapě nejsou.
  // Nic nepřepisuje, jen je přidá na konec a rovnou uloží.
  // 28.9.2026 (hlášení #125): doplnění proběhlo hned, ale nikde to nebylo vidět
  // a Láďa nevěděl, jestli se něco stalo. Teď se po uložení ukáže hláška.
  const doplnitChybejici = () => {
    if (!chybejici.length) return;
    const kolik = chybejici.length;
    uloz([...pages, ...chybejici], () => {
      setChybejici([]);
      toast(kolik === 1 ? "Do mapy přibyla 1 stránka." : `Do mapy přibylo ${kolik} stránek.`, "success");
    });
  };

  const videt = useMemo(() => {
    const q = hledej.trim().toLowerCase();
    return pages.filter((p) => {
      if (p.id === upravovane) return true;
      if (filtrIndex !== "vse" && (p.indexace || "n/a") !== filtrIndex) return false;
      if (!q) return true;
      return [p.nazev, p.url, p.ucel, p.seoTitle, p.seoDesc, p.kody, p.poznamka]
        .join(" ").toLowerCase().includes(q);
    });
  }, [pages, hledej, filtrIndex, upravovane]);

  const pocetIndex = pages.filter((p) => p.indexace === "index").length;

  if (loading && !pages.length) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Stranka>
      <GradientHeader
        variant="purple"
        icon={Globe}
        eyebrow="Web"
        title="Mapa webu"
        napoveda="Přehled stránek fitway40.cz."
      >
        <button
          type="button"
          onClick={nova}
          disabled={!!upravovane}
          className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-brand-800 transition hover:bg-white/90 disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          <Plus className="w-4 h-4" /> Nová stránka
        </button>
      </GradientHeader>

      {err && (
        <ErrorNote>{err}</ErrorNote>
      )}

      {chybejici.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="min-w-0 text-sm text-amber-900">
            <div className="font-semibold">
              Na webu {chybejici.length === 1 ? "je 1 stránka, kterou tu nemáte" : `je ${chybejici.length} stránek, které tu nemáte`}
            </div>
            <div className="mt-0.5 text-amber-800">{chybejici.map((c) => c.nazev).join(", ")}</div>
          </div>
          <button
            type="button"
            onClick={doplnitChybejici}
            disabled={saving}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900 transition hover:bg-amber-100 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            <DownloadCloud className="w-4 h-4" /> {saving ? "Doplňuji…" : "Doplnit do mapy"}
          </button>
        </div>
      )}

      {/* Filtry */}
      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={hledej}
            onChange={(e) => setHledej(e.target.value)}
            placeholder="Hledat v mapě webu"
            className="field w-full pl-9 pr-3"
          />
        </div>
        <select
          value={filtrIndex}
          onChange={(e) => setFiltrIndex(e.target.value)}
          className="field w-auto"
        >
          <option value="vse">Všechny stránky</option>
          <option value="index">Jen indexované</option>
          <option value="noindex">Jen noindex</option>
          <option value="n/a">Bez indexace</option>
        </select>
        <div className="flex shrink-0 items-center gap-2 px-1 text-xs">
          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">{videt.length} z {pages.length}</span>
          <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700">{pocetIndex} na Googlu</span>
        </div>
      </div>

      {/* Seznam */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="hidden items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:flex">
          <span className="w-4" />
          <span className="flex-1">Stránka</span>
          <span className="w-64 shrink-0">Účel</span>
          <span className="w-24 shrink-0 text-right">Indexace</span>
        </div>

        {videt.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">Žádná stránka neodpovídá filtru.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {videt.map((p) => {
              const rozbaleno = otevrene === p.id;
              const edituje = upravovane === p.id;
              const d = edituje ? koncept : p;
              const idx = d.indexace || "n/a";

              return (
                <div
                  key={p.id}
                  ref={(el) => { detailRefs.current[p.id] = el; }}
                  className={`relative scroll-mt-24 ${rozbaleno ? "bg-slate-50/60" : ""}`}
                >
                  <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${INDEX_PRUH[idx] || INDEX_PRUH["n/a"]}`} />

                  {/* ŘÁDEK */}
                  <button
                    type="button"
                    onClick={() => prepnout(p.id)}
                    className="flex w-full items-center gap-3 px-4 py-3 pl-5 text-left transition hover:bg-slate-50"
                  >
                    <span className="shrink-0 text-slate-400">
                      {rozbaleno ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-slate-900">{d.nazev || "(bez názvu)"}</span>
                      <span className="mt-0.5 block truncate font-mono text-xs text-slate-500">{kratkaUrl(d.url)}</span>
                    </span>

                    <span className="hidden w-64 shrink-0 truncate text-sm text-slate-600 sm:block">{d.ucel || "—"}</span>

                    <span className="flex w-24 shrink-0 justify-end">
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${INDEX_BADGE[idx] || INDEX_BADGE["n/a"]}`}>
                        {idx}
                      </span>
                    </span>
                  </button>

                  {/* DETAIL */}
                  {rozbaleno && (
                    <div className="border-t border-slate-200 bg-white px-4 py-4 pl-5">
                      {!edituje ? (
                        <div className="space-y-3">
                          {d.url && (
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 break-all text-sm font-medium text-brand-600 hover:text-brand-800"
                            >
                              {d.url} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                            </a>
                          )}

                          {d.ucel && (
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Účel / funnel</div>
                              <p className="mt-1 text-sm leading-relaxed text-slate-800">{d.ucel}</p>
                            </div>
                          )}

                          <div className="grid gap-3 sm:grid-cols-2">
                            {d.seoTitle && (
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">SEO titulek</div>
                                <p className="mt-1 text-sm text-slate-700">{d.seoTitle}</p>
                              </div>
                            )}
                            {d.seoDesc && (
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">SEO popis</div>
                                <p className="mt-1 text-sm text-slate-700">{d.seoDesc}</p>
                              </div>
                            )}
                          </div>

                          {d.kody && (
                            <div className="rounded-xl bg-slate-50 px-3.5 py-2.5">
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Marketingové kódy</div>
                              <p className="mt-1 text-sm text-slate-700">{d.kody}</p>
                            </div>
                          )}

                          {d.poznamka && (
                            <p className="text-xs leading-relaxed text-slate-500">
                              <span className="font-semibold uppercase tracking-wide text-slate-400">Pozn.: </span>{d.poznamka}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => zacitUpravu(p)}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                            >
                              <Pencil className="w-3.5 h-3.5" /> Upravit
                            </button>
                            {confirmDel === p.id ? (
                              <span className="inline-flex items-center gap-2 text-sm">
                                <span className="text-slate-500">Opravdu smazat?</span>
                                <button type="button" onClick={() => smaz(p.id)} disabled={saving} className="btn btn-danger">Ano, smazat</button>
                                <button type="button" onClick={() => setConfirmDel(null)} className="rounded-xl bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200">Zpět</button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDel(p.id)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-800"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Smazat
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            {POLE.map((f) => (
                              <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</label>
                                {f.type === "textarea" ? (
                                  <textarea
                                    value={koncept[f.key] || ""}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    rows={2}
                                    className="field w-full resize-y"
                                  />
                                ) : f.type === "select" ? (
                                  <select
                                    value={koncept[f.key] || "index"}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    className="field w-full"
                                  >
                                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={koncept[f.key] || ""}
                                    onChange={(e) => setKoncept((k) => ({ ...k, [f.key]: e.target.value }))}
                                    className="field w-full"
                                  />
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={ulozKoncept}
                              disabled={saving}
                              className="btn btn-primary"
                            >
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit
                            </button>
                            <button
                              type="button"
                              onClick={() => { zrusitUpravu(); load(); }}
                              disabled={saving}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                            >
                              <X className="w-4 h-4" /> Zrušit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Stranka>
  );
}
